import { jobListQuerySchema, type JobListQuery, type Page } from '@chukua/common';
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { toListParams } from '../../../shared/policies/list-params';
import { listJobs, type JobListItem } from '../../jobs/jobs.api';

export type JobQueueState =
  { status: 'loading' } | { status: 'loaded'; page: Page<JobListItem>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

/** Only jobs a dispatcher can act on: confirmed, or planned with trips still to crew. */
const QUEUE_STATUSES: JobListQuery['status'] = ['confirmed', 'planned'];

/** The server refuses a multi-value filter of more than 50 values (`listFilter`), so only the 50 most recent skips are sent; older ones show again. */
const MAX_EXCLUDED_JOBS = 50;

const keyOf = (query: JobListQuery) => toListParams(query).toString();

/** The dispatch queue: the URL's list query (search, sort, job type, page) with the status fixed and skipped jobs left out server-side, so paging and the total stay right. */
export function useJobQueue(excludedJobIds: string[]) {
  const list = useListQuery(jobListQuerySchema);
  const [state, setState] = useState<JobQueueState>({ status: 'loading' });
  const [reloads, setReloads] = useState(0);

  const requestQuery = useMemo<JobListQuery>(
    () => ({ ...list.query, status: QUEUE_STATUSES, excludeId: excludedJobIds.length > 0 ? excludedJobIds.slice(-MAX_EXCLUDED_JOBS) : undefined }),
    [list.query, excludedJobIds],
  );

  useEffect(() => {
    let cancelled = false;
    listJobs(requestQuery)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page, queryKey: keyOf(requestQuery) });
      })
      .catch((err) => {
        if (!cancelled) setState({ status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [requestQuery, reloads]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(requestQuery);

  function reload(): void {
    setState({ status: 'loading' });
    setReloads((count) => count + 1);
  }

  return { state, list, isRefreshing, reload };
}
